import React, { useState, useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Box, Typography, Chip,
  makeStyles, CircularProgress, IconButton, Paper, Divider
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import ViewCarouselIcon from '@material-ui/icons/ViewCarousel';
import DateRangeIcon from '@material-ui/icons/DateRange';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';

import MultiLangTextField from '../../components/MultiLangTextField';
import { getBannerById, createBanner, updateBanner } from '../../services/bannerService';

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      margin: 24,
      width: 'calc(100vw - 48px)',
      maxWidth: 'calc(100vw - 48px) !important',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: 20,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.type === 'dark' ? '#0f172a' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 25px 70px rgba(0,0,0,0.35)',
      '@media (max-width: 600px)': {
        margin: 8,
        width: 'calc(100vw - 16px)',
        height: 'calc(100vh - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        borderRadius: 14,
      },
    },
  },
  dialogHeader: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.type === 'dark' ? '#111827' : '#f8fafc',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.2) 100%)',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    borderRadius: 10,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  dialogContent: {
    padding: '24px 28px',
    overflowY: 'auto',
    flexGrow: 1,
    background: theme.palette.type === 'dark' ? '#0b1120' : '#f8fafc',
  },
  dialogActions: {
    padding: '14px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#111827' : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    flexShrink: 0,
  },
  sectionCard: {
    padding: 24,
    borderRadius: 16,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: 20,
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
  },
  sectionDesc: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  previewCard: {
    padding: theme.spacing(2.5),
    borderRadius: 20,
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(30,41,59,0.9) 100%)'
      : 'linear-gradient(135deg, #E8F5E9 0%, #FFFFFF 100%)',
    border: '1.5px solid rgba(16,185,129,0.3)',
    boxShadow: '0 8px 24px rgba(5,150,105,0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  previewIcon: {
    width: 46,
    height: 46,
    borderRadius: '50%',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(5,150,105,0.2)',
    color: '#059669',
  },
  field: {
    marginBottom: theme.spacing(2.5),
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
      borderRadius: 10,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
    },
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#ffffff',
    fontWeight: 600,
    borderRadius: 10,
    padding: '8px 24px',
    boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
  },
  cancelBtn: {
    borderRadius: 10,
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
  },
}));

function toDateInputValue(timestamp) {
  if (!timestamp) return '';
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return d.toISOString().split('T')[0];
}

const emptyMulti = { en: '', bn: '', ar: '' };

export default function BannerFormModal({ open, onClose, bannerId = null, onSuccess }) {
  const classes = useStyles();
  const theme = useTheme();

  const isEdit = Boolean(bannerId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [previewLang, setPreviewLang] = useState('en');

  const [form, setForm] = useState({
    title: { ...emptyMulti },
    shortText: { ...emptyMulti },
    description: { ...emptyMulti },
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    if (!open) return;
    setErrorMsg('');
    if (bannerId) {
      setLoading(true);
      getBannerById(bannerId)
        .then((data) => {
          if (data) {
            const readMulti = (key) => ({
              en: data[key]?.en || data[`${key}.en`] || '',
              bn: data[key]?.bn || data[`${key}.bn`] || '',
              ar: data[key]?.ar || data[`${key}.ar`] || '',
            });
            setForm({
              title: readMulti('title'),
              shortText: readMulti('shortText'),
              description: readMulti('description'),
              startDate: toDateInputValue(data.startDate),
              endDate: toDateInputValue(data.endDate),
            });
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setErrorMsg('Failed to load banner details.');
          setLoading(false);
        });
    } else {
      setForm({
        title: { ...emptyMulti },
        shortText: { ...emptyMulti },
        description: { ...emptyMulti },
        startDate: '',
        endDate: '',
      });
      setLoading(false);
    }
  }, [open, bannerId]);

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!form.title.en.trim() || !form.shortText.en.trim() || !form.description.en.trim() || !form.startDate || !form.endDate) {
      setErrorMsg('Please complete all required fields (English title, short text, description, and dates).');
      return;
    }

    if (new Date(form.endDate) <= new Date(form.startDate)) {
      setErrorMsg('End date must be after start date.');
      return;
    }

    setSaving(true);
    setErrorMsg('');
    try {
      if (isEdit) {
        await updateBanner(bannerId, form);
      } else {
        await createBanner(form);
      }
      setSaving(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || 'Failed to save banner.');
      setSaving(false);
    }
  };

  const liveTitle = form.title[previewLang] || form.title.en || 'Banner Headline Title';
  const liveShort = form.shortText[previewLang] || form.shortText.en || 'Short preview teaser shown on the app Home screen...';

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className={classes.dialogRoot}
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
      scroll="paper"
    >
      <DialogTitle disableTypography className={classes.dialogHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIcon}>
            <ViewCarouselIcon />
          </div>
          <div>
            <Typography className={classes.headerTitle}>
              {isEdit ? 'ব্যানার সম্পাদনা (Edit Home Banner)' : 'নতুন হোম ব্যানার যোগ করুন (Create Banner)'}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {isEdit ? `ID: ${bannerId} • ${liveTitle}` : 'অ্যাপ হোম স্ক্রিনে প্রদর্শনের জন্য ফিচার্ড ব্যানার তৈরি করুন'}
            </Typography>
          </div>
        </div>

        <IconButton
          onClick={onClose}
          className={classes.closeBtn}
          size="small"
          disabled={saving}
          title="Close Modal"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
            <CircularProgress style={{ color: '#10b981' }} size={48} thickness={4} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              ব্যানারের তথ্য লোড হচ্ছে...
            </Typography>
          </Box>
        ) : (
          <>
            {errorMsg && (
              <Box mb={2.5} p={1.5} borderRadius={8} bgcolor="rgba(239,68,68,0.1)" border="1px solid rgba(239,68,68,0.3)">
                <Typography variant="body2" style={{ color: '#ef4444', fontWeight: 600 }}>
                  <span role="img" aria-label="error">⚠️</span> {errorMsg}
                </Typography>
              </Box>
            )}

            {/* LIVE PREVIEW SECTION */}
            <Paper elevation={0} className={classes.sectionCard}>
              <div className={classes.sectionHeader}>
                <PhoneAndroidIcon style={{ color: '#10b981' }} />
                <Typography className={classes.sectionTitle}>
                  Live App Home Preview (লাইভ প্রিভিউ)
                </Typography>
              </div>
              <Typography className={classes.sectionDesc}>
                This shows how the banner will look directly inside the mobile app Home screen card.
              </Typography>

              <Box mb={2} display="flex" style={{ gap: 8 }}>
                {['en', 'bn', 'ar'].map((lang) => (
                  <Chip
                    key={lang}
                    size="small"
                    label={lang === 'en' ? 'English' : lang === 'bn' ? 'বাংলা' : 'العربية'}
                    clickable
                    color={previewLang === lang ? 'primary' : 'default'}
                    onClick={() => setPreviewLang(lang)}
                    style={{ fontWeight: 600 }}
                  />
                ))}
              </Box>

              <div className={classes.previewCard}>
                <div className={classes.previewIcon}>
                  <ViewCarouselIcon />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" style={{ color: '#059669', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Featured Daily Message
                  </Typography>
                  <Typography variant="subtitle1" style={{ fontWeight: 700, color: theme.palette.type === 'dark' ? '#f1f5f9' : '#0f172a', lineHeight: 1.3 }}>
                    {liveTitle}
                  </Typography>
                  <Typography variant="body2" style={{ color: theme.palette.text.secondary, marginTop: 4 }}>
                    {liveShort}
                  </Typography>
                </div>
              </div>
            </Paper>

            {/* MULTI-LANG CONTENT */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 16 }}>
                ব্যানার কন্টেন্ট ও বিবরণ (Banner Content)
              </Typography>

              <Box mb={3}>
                <MultiLangTextField
                  label="Banner Headline Title"
                  title="Banner Title"
                  fields={form.title}
                  onChange={(val) => setForm({ ...form, title: val })}
                  required
                />
              </Box>

              <Divider style={{ margin: '20px 0' }} />

              <Box mb={3}>
                <MultiLangTextField
                  label="Short Card Teaser"
                  title="Short Card Teaser Text"
                  fields={form.shortText}
                  onChange={(val) => setForm({ ...form, shortText: val })}
                  required
                />
              </Box>

              <Divider style={{ margin: '20px 0' }} />

              <Box mb={1}>
                <MultiLangTextField
                  label="Full Extended Description"
                  title="Full Description (Shown on Tap)"
                  fields={form.description}
                  onChange={(val) => setForm({ ...form, description: val })}
                  multiline
                  minRows={3}
                  required
                />
              </Box>
            </Paper>

            {/* SCHEDULE SECTION */}
            <Paper elevation={0} className={classes.sectionCard}>
              <div className={classes.sectionHeader}>
                <DateRangeIcon style={{ color: '#10b981' }} />
                <Typography className={classes.sectionTitle}>
                  Schedule &amp; Visibility Window (সময়সূচী)
                </Typography>
              </div>
              <Typography className={classes.sectionDesc}>
                Set the active time frame when this banner is automatically shown on users' devices.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    variant="outlined"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    className={classes.field}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    variant="outlined"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    className={classes.field}
                    required
                  />
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} className={classes.cancelBtn} disabled={saving} style={{ marginRight: 8 }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          className={classes.saveBtn}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? 'সংরক্ষণ হচ্ছে...' : isEdit ? 'আপডেট করুন (Save Changes)' : 'ব্যানার সংরক্ষণ করুন (Save Banner)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
